/**
 * Session and permissions, read from Open edX.
 *
 * Authentik answers "who are you" over OIDC; Open edX answers "what can you do".
 * They are not synced, so authority is never inferred from a token — it comes from
 * the tenancy app, which reads is_superuser and the org roles out of the database.
 */

import { openedx, lmsOrigin } from './openedx';
import { getConfig } from './config';

export type Persona = 'anonymous' | 'learner' | 'tenant_staff' | 'tenant_admin' | 'superadmin';

export interface Session {
  authenticated: boolean;
  user: { username: string; email: string; name: string; avatar: string | null } | null;
  permissions: {
    isSuperuser: boolean;
    isStaff: boolean;
    /** Orgs where the user holds OrgInstructorRole — tenant administrators. */
    adminOrgs: string[];
    /** Orgs where the user holds OrgStaffRole — tenant staff. */
    staffOrgs: string[];
    staffCourses: string[];
  };
}

export const ANONYMOUS: Session = {
  authenticated: false,
  user: null,
  permissions: {
    isSuperuser: false,
    isStaff: false,
    adminOrgs: [],
    staffOrgs: [],
    staffCourses: [],
  },
};

export async function loadSession(): Promise<Session> {
  // Cheap probe first: /me returns the username or 401. Asking for roles before we
  // know there is a user would 401 on every anonymous page load.
  const me = await openedx.get<{ username: string } | null>('/api/user/v1/me', true);
  if (!me?.username) return ANONYMOUS;

  const roles = await openedx.get<Session | null>(
    `/api/tenant/v1/users/${encodeURIComponent(me.username)}/roles`,
    true,
  );

  // Degrade to learner rather than guessing upward if roles are unavailable.
  return roles ?? { ...ANONYMOUS, authenticated: true, user: { username: me.username, email: '', name: me.username, avatar: null } };
}

/**
 * Highest persona this session holds for the current tenant.
 *
 * Mirrors `persona_for` in the tenancy app. This copy drives navigation only — the
 * API re-checks authority on every request and is the real boundary.
 */
export function personaOf(session: Session, tenantOrgs: string[]): Persona {
  if (!session.authenticated) return 'anonymous';
  if (session.permissions.isSuperuser) return 'superadmin';

  const orgs = new Set(tenantOrgs);
  if (session.permissions.adminOrgs.some((o) => orgs.has(o))) return 'tenant_admin';
  if (session.permissions.staffOrgs.some((o) => orgs.has(o))) return 'tenant_staff';
  return 'learner';
}

/** Start SSO sign-in. With OIDC primary this is a redirect, not a form. */
export function signIn(next: string = window.location.href): void {
  const { auth } = getConfig();
  window.location.assign(`${lmsOrigin}${auth.oidcEntrypoint}?next=${encodeURIComponent(next)}`);
}

/**
 * Sign in with a password.
 *
 * MUST be the v2 endpoint. v1 requires a field literally named `email` and matches
 * by email only; v2 accepts `email_or_username` and falls back to username lookup.
 * Posting `email_or_username` to v1 fails for every user with "There was an error
 * receiving your login information."
 */
export async function signInWithPassword(
  emailOrUsername: string,
  password: string,
): Promise<void> {
  await openedx.postForm('/api/user/v2/account/login_session/', {
    email_or_username: emailOrUsername,
    password,
  });
}

export function signOut(): void {
  window.location.assign(`${lmsOrigin}/logout`);
}
